import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  RefreshCw,
  Search,
  X,
  User,
  BookOpen,
  Award,
  ChevronRight,
  Filter,
  Hash,
} from "lucide-react";

import {
  useAdminListApplicationsQuery,
  useAdminGradeApplicationMutation,
} from "../../api/examsApi";

// Security & UI Patterns
import PageGate from "../../../../components/PageGate";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function safeMsg(err: any) {
  return (
    err?.data?.message || err?.error || err?.message || "Something went wrong"
  );
}

/** * Aesthetic Tooltip */
function Tooltip({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[60] pointer-events-none animate-in fade-in zoom-in-95 duration-100">
        <div className="bg-gray-900 text-white text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1.5 rounded shadow-xl whitespace-nowrap border border-gray-700">
          {text}
        </div>
        <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1 border-r border-b border-gray-700"></div>
      </div>
    </div>
  );
}

const GRADE_ROLES = ["examiner", "committee_examination_board.chairman"];

export default function AdminGradingPage() {
  const { can } = useAccess();
  const canGrade = can.anyRole(GRADE_ROLES);

  const [status, setStatus] = useState("submitted");
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data = [],
    isLoading,
    refetch,
    isFetching,
  } = useAdminListApplicationsQuery({ status });

  const [active, setActive] = useState<any | null>(null);
  const [grade, { isLoading: grading }] = useAdminGradeApplicationMutation();

  const [paperMap, setPaperMap] = useState<
    Record<string, { status: string; score: string }>
  >({});
  const [remarks, setRemarks] = useState("");

  // Memoized search filtering
  const rows = useMemo(() => {
    const list = data || [];
    if (!searchQuery.trim()) return list;

    const term = searchQuery.toLowerCase();
    return list.filter(
      (a: any) =>
        a.exam?.title?.toLowerCase().includes(term) ||
        a.exam?.code?.toLowerCase().includes(term) ||
        a.center?.name?.toLowerCase().includes(term) ||
        a.id?.toLowerCase().includes(term),
    );
  }, [data, searchQuery]);

  function openGrade(app: any) {
    setActive(app);
    const m: any = {};
    for (const p of app?.papers || []) {
      const pid = p.exam_paper_id ?? p.paper?.id;
      if (!pid) continue;
      m[pid] = {
        status: p.status === "pending" ? "fail" : p.status,
        score: p.score != null ? String(p.score) : "",
      };
    }
    setPaperMap(m);
    setRemarks("");
  }

  function updatePaper(
    paperId: string,
    patch: Partial<{ status: string; score: string }>,
  ) {
    setPaperMap((prev) => ({
      ...prev,
      [paperId]: {
        ...(prev[paperId] || { status: "fail", score: "" }),
        ...patch,
      },
    }));
  }

  async function submitGrade() {
    if (!active) return;
    const papers = (active.papers || []).map((p: any) => {
      const paperId = p.exam_paper_id ?? p.paper?.id;
      return {
        exam_paper_id: paperId,
        status: paperMap[paperId]?.status || "fail",
        score: paperMap[paperId]?.score ? Number(paperMap[paperId].score) : null,
      };
    });

    try {
      await grade({
        applicationId: active.id,
        body: { papers, remarks: remarks.trim() || undefined },
      }).unwrap();
      setActive(null);
      setPaperMap({});
      setRemarks("");
      refetch();
    } catch (e) {
      alert(safeMsg(e));
    }
  }

  return (
    <PageGate perm={PERMS.exam.gradeAccess}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Industrial Header */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl">
                <ClipboardCheck className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                  Exam Grading
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  Evaluation gateway for candidate applications and paper
                  scores.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/exams/admin/exams"
                className="px-5 py-2.5 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
              >
                Global List
              </Link>
              <AccessGate perm={PERMS.exam.dietEdit}>
                <Tooltip text="Sync Applications">
                  <button
                    onClick={() => refetch()}
                    className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                  >
                    <RefreshCw
                      className={`w-4 h-4 text-indigo-500 ${
                        isFetching ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                </Tooltip>
              </AccessGate>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 2. Filter Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6">
              <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                <Filter className="w-4 h-4 text-indigo-500" />
                <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                  Queue Filters
                </div>
              </div>

              <div className="space-y-6">
                {/* Search Input */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Search Candidate
                  </label>
                  <div className="relative">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300" />
                    <input
                      type="text"
                      placeholder="Exam, Code, or Center..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-5 border-b-2 border-gray-50 py-2 text-[11px] font-bold text-gray-600 outline-none focus:border-indigo-400 bg-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  >
                    <option value="submitted">Submitted Queue</option>
                    <option value="completed">Completed Registry</option>
                  </select>
                </div>

                <div className="pt-4 border-t border-gray-50">
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      Results Found
                    </div>
                    <div className="text-2xl font-black text-gray-900">
                      {rows.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Applications List */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 flex flex-col items-center justify-center">
                <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
                  Fetching Candidates...
                </span>
              </div>
            ) : rows.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 text-center">
                <Search className="w-12 h-12 mx-auto text-gray-100 mb-4" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                  No records match your filter criteria
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {rows.map((a: any) => (
                  <div
                    key={a.id}
                    className="group bg-white border border-gray-100 rounded-[2rem] p-6 hover:shadow-xl hover:shadow-gray-100 transition-all flex flex-col md:flex-row items-center justify-between gap-6"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-black text-gray-900 uppercase tracking-tight text-lg leading-none">
                            {a.exam?.title || "Exam Unit"}
                          </h3>
                          <span className="text-[10px] font-black px-2 py-0.5 bg-gray-100 rounded text-gray-500 uppercase tracking-widest">
                            {a.exam?.code}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter flex items-center gap-1.5">
                            <BookOpen className="w-3 h-3" />{" "}
                            {a.diet?.title || "—"}
                          </div>
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter flex items-center gap-1.5">
                            <Award className="w-3 h-3" />{" "}
                            {a.center?.name || "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 w-full md:w-auto border-t md:border-t-0 pt-4 md:pt-0">
                      <div className="text-right hidden md:block">
                        <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                          Application ID
                        </div>
                        <div className="text-[10px] font-bold text-gray-900 uppercase">
                          {a.id.slice(0, 8)}...
                        </div>
                        {a.result?.overall_status && (
                          <div className="mt-2">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                              a.result.overall_status === "recommended"
                                ? "bg-teal-50 text-teal-700 border-teal-100"
                                : a.result.overall_status === "not_recommended"
                                ? "bg-orange-50 text-orange-700 border-orange-100"
                                : a.result.overall_status === "passed"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-rose-50 text-rose-700 border-rose-100"
                            }`}>
                              {a.result.overall_status.replace(/_/g, " ")}
                            </span>
                            {a.result.remarks && (
                              <p className="text-[9px] font-medium text-gray-400 italic mt-1 max-w-[140px] line-clamp-2 text-right" title={a.result.remarks}>
                                {a.result.remarks}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {!["PCI", "MRI"].includes(a.exam?.code) && (
                        <Link
                          to={`/exams/admin/applications/${a.id}/courses`}
                          className="flex-none px-5 py-3 bg-white border border-gray-200 text-gray-700 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-gray-50 transition-all shadow-sm active:scale-95 flex items-center justify-center gap-2"
                        >
                          <BookOpen className="w-3 h-3" /> Courses
                        </Link>
                      )}
                      {canGrade && (["PCI", "MRI"].includes(a.exam?.code) ? (
                        <button
                          onClick={() => openGrade(a)}
                          className="flex-1 md:flex-none px-8 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                        >
                          Evaluate <ChevronRight className="w-3 h-3" />
                        </button>
                      ) : (
                        <Link
                          to={`/exams/admin/applications/${a.id}/course-grading`}
                          className="flex-1 md:flex-none px-8 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                        >
                          Grade Courses <ChevronRight className="w-3 h-3" />
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 4. Policy Footer */}
        <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
            <Award className="w-10 h-10 text-indigo-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
              Grading Compliance
            </h4>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Score entries must strictly adhere to the{" "}
              <span className="text-white">INSTITUTIONAL RUBRIC</span>. Status
              updates to <span className="text-white">FAIL</span> or{" "}
              <span className="text-white">ABSENT</span> will trigger automatic
              eligibility for the subsequent diet cycle registration.
            </p>
          </div>
        </div>

        {/* 5. Grading Slide-out Modal */}
        {active && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300"
              onClick={() => setActive(null)}
            />
            <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500">
              {/* Modal Header */}
              <div className="p-8 border-b border-gray-50 bg-gray-50/30">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mb-2">
                      Evaluation Active
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
                      {active.exam?.title}
                    </h3>
                    <div className="flex gap-4 mt-2">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                        <Hash className="w-3 h-3" /> {active.id.slice(0, 12)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActive(null)}
                    className="p-3 bg-white border border-gray-100 rounded-2xl hover:bg-rose-50 hover:text-rose-600 transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-8 space-y-6">
                {(active.papers || []).map((p: any) => {
                  const paperId = p.exam_paper_id ?? p.paper?.id;
                  const val = paperMap[paperId] || {
                    status: "fail",
                    score: "",
                  };

                  return (
                    <div
                      key={paperId}
                      className="bg-white border border-gray-100 rounded-[1.5rem] p-6 shadow-sm"
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">
                              Paper Identity
                            </div>
                            <div className="font-black text-gray-900 uppercase tracking-tight text-sm">
                              {p.paper?.title || "Unnamed Paper"}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-1">
                              Code
                            </div>
                            <div className="font-black text-gray-900 text-xs">
                              {p.paper?.code || "N/A"}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                              Outcome
                            </label>
                            <select
                              value={val.status}
                              onChange={(e) =>
                                updatePaper(paperId, { status: e.target.value })
                              }
                              className={`w-full border-2 rounded-xl px-4 py-2 text-[10px] font-black uppercase tracking-widest outline-none transition-all ${
                                val.status === "pass"
                                  ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                                  : val.status === "absent"
                                  ? "border-gray-100 bg-gray-50 text-gray-600"
                                  : "border-rose-100 bg-rose-50 text-rose-700"
                              }`}
                            >
                              <option value="pass">Pass</option>
                              <option value="fail">Fail</option>
                              <option value="absent">Absent</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                              Score
                            </label>
                            <input
                              type="text"
                              placeholder="0-100"
                              value={val.score}
                              onChange={(e) =>
                                updatePaper(paperId, { score: e.target.value })
                              }
                              className="w-full border-2 border-gray-100 rounded-xl px-4 py-2 text-xs font-black text-gray-800 focus:border-indigo-500 outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Overall remarks */}
                <div className="space-y-2 pt-2 border-t border-gray-50">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Examiner Remarks{" "}
                    <span className="normal-case font-medium text-gray-300">(optional, max 50 words)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Overall examiner notes for this candidate…"
                    className="w-full border-2 border-gray-100 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 focus:border-indigo-400 outline-none resize-none"
                  />
                  <div className="text-right text-[9px] font-bold text-gray-300">
                    {remarks.trim().split(/\s+/).filter(Boolean).length} / 50 words
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-8 border-t border-gray-50 bg-white">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActive(null)}
                    className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400"
                  >
                    Cancel
                  </button>
                  {/* AccessGate guards the final submission action */}
                  <AccessGate perm={PERMS.exam.gradeToggle}>
                    <button
                      disabled={grading || (active.papers || []).length === 0}
                      onClick={submitGrade}
                      className="flex-[2] bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30"
                    >
                      {grading ? "Synchronizing..." : "Commit Evaluation"}
                    </button>
                  </AccessGate>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
